/**
 * Google AdSense Service
 * Manages manual ad placements and AdSense functionality
 */

class AdSenseService {
  constructor() {
    this.initialized = false;
    this.adUnits = new Map();
  }

  /**
   * Initialize AdSense (called when the script loads)
   */
  initialize() {
    if (typeof window !== 'undefined' && window.adsbygoogle) {
      this.initialized = true;
      console.log('AdSense initialized successfully');
      return true;
    }
    return false;
  }

  /**
   * Check if AdSense is available
   */
  isAvailable() {
    return this.initialized && typeof window !== 'undefined' && window.adsbygoogle;
  }

  /**
   * Push an ad to AdSense for display
   * @param {string} adSlot - The ad slot ID
   * @param {HTMLElement} element - The DOM element containing the ad
   */
  pushAd(adSlot, element) {
    if (!this.isAvailable()) {
      console.warn('AdSense not available');
      return false;
    }

    // Check if element has proper dimensions before pushing
    // In flex layouts, the <ins> can initially measure 0 width; use parent width as fallback
    const measuredWidth = element?.offsetWidth || element?.parentElement?.offsetWidth || 0;
    if (measuredWidth === 0) {
      console.warn(`Ad container has zero width for slot: ${adSlot}. Retrying...`);
      // Retry after a short delay to allow layout to settle
      setTimeout(() => {
        this.pushAd(adSlot, element);
      }, 200);
      return false;
    }

    try {
      // Store the ad unit reference
      this.adUnits.set(adSlot, element);

      // Push the ad to AdSense
      (window.adsbygoogle = window.adsbygoogle || []).push({});

      console.log(`Ad pushed for slot: ${adSlot}`);
      return true;
    } catch (error) {
      console.error('Error pushing ad to AdSense:', error);
      return false;
    }
  }

  /**
   * Refresh ads (useful for SPA navigation)
   */
  refreshAds() {
    if (!this.isAvailable()) {
      return false;
    }

    try {
      // Clear previous ads
      this.adUnits.clear();

      // Re-initialize ads on the page
      const adElements = document.querySelectorAll('.adsbygoogle');
      adElements.forEach((element) => {
        if (element.getAttribute('data-adsbygoogle-status') !== 'done') {
          (window.adsbygoogle = window.adsbygoogle || []).push({});
        }
      });

      return true;
    } catch (error) {
      console.error('Error refreshing ads:', error);
      return false;
    }
  }

  /**
   * Get ad unit by slot ID
   */
  getAdUnit(adSlot) {
    return this.adUnits.get(adSlot);
  }

  /**
   * Remove an ad unit
   */
  removeAdUnit(adSlot) {
    const element = this.adUnits.get(adSlot);
    if (element) {
      element.innerHTML = '';
      this.adUnits.delete(adSlot);
    }
  }

  /**
   * Check if the current environment should show ads
   */
  shouldShowAds() {
    if (typeof window === 'undefined') return false;

    // Don't show ads in development
    const isDevelopment = window.location.hostname === 'localhost' ||
                         window.location.hostname.includes('staging') ||
                         window.location.hostname.includes('development');

    return !isDevelopment;
  }
}

// Create singleton instance
const adsenseService = new AdSenseService();

// Initialize when AdSense script loads
if (typeof window !== 'undefined') {
  // Check if already loaded
  if (window.adsbygoogle) {
    adsenseService.initialize();
  } else {
    // Wait for script to load
    const checkAdSense = () => {
      if (window.adsbygoogle) {
        adsenseService.initialize();
      } else {
        setTimeout(checkAdSense, 100);
      }
    };
    checkAdSense();
  }
}

export default adsenseService;
